import { If, Media } from "components";
import { t } from "i18next";
import { DirectUploadMedia } from "libs/directUpload";
import { useGetOrderDetails } from "modules/orders";
import { ReactNode } from "react";
import Skeleton from "react-loading-skeleton";
import { Button } from "ui";

import { formatAmountInFcfa } from "../../../helpers";

import styles from "./ReferralOrders.module.scss";

interface ReferralOrderProductsProps {
  orderId: string;
  onOpenOrder: (orderId: string) => void;
}

interface ProductLineProps {
  image?: DirectUploadMedia | null;
  name: string;
  subtitle: ReactNode;
  quantity: number;
  unitPrice: number;
  total: number;
}

/**
 * `Media` dereferences its file, so a variant without an image would take the
 * whole panel down with it — hence the guard and the placeholder.
 */
const ProductLine = ({
  image,
  name,
  subtitle,
  quantity,
  unitPrice,
  total
}: ProductLineProps) => (
  <div className={styles.products_item}>
    <div className={styles.products_product}>
      <div className={styles.products_thumb}>
        <If condition={!!image}>
          <Media
            className={styles.products_thumbMedia}
            mediaFile={image as DirectUploadMedia}
          />
        </If>
      </div>

      <div className={styles.products_text}>
        <span className={styles.products_name}>{name}</span>
        <span className={styles.products_subtitle}>{subtitle}</span>
      </div>
    </div>

    <span className={styles.products_quantity}>
      {`${quantity} × ${formatAmountInFcfa(unitPrice)}`}
    </span>

    <span className={styles.products_total}>{formatAmountInFcfa(total)}</span>
  </div>
);

/**
 * The deepest level of the drill-down. There is no dedicated endpoint for it:
 * the line items come from the order details call, fetched only once the row is
 * actually expanded.
 */
const ReferralOrderProducts = ({
  orderId,
  onOpenOrder
}: ReferralOrderProductsProps) => {
  const { orderDetails, isLoading } = useGetOrderDetails(orderId);

  if (isLoading) {
    return <Skeleton height={48} count={2} />;
  }

  if (!orderDetails?.items?.length) {
    return <div className={styles.products}>{t("No products")}</div>;
  }

  return (
    <div className={styles.products}>
      {orderDetails.items.map((item) => {
        const variant = item.barcode?.productVariant;

        return (
          <ProductLine
            key={item.id}
            image={variant?.image}
            name={variant?.name ?? "—"}
            subtitle={[variant?.sku, item.barcode?.sizeAttributeValue?.value]
              .filter(Boolean)
              .join(" · ")}
            quantity={item.quantity}
            unitPrice={item.discountedPricePerItem}
            total={item.totalPricePerItem}
          />
        );
      })}

      <div className={styles.products_footer}>
        <span>
          {`${t("Subtotal")}: ${formatAmountInFcfa(
            orderDetails.totalOrder - orderDetails.deliveryFee
          )}`}
        </span>
        <span>{`${t("Delivery")}: ${formatAmountInFcfa(orderDetails.deliveryFee)}`}</span>
        <span>{`${t("Total paid")}: ${formatAmountInFcfa(orderDetails.totalOrder)}`}</span>
      </div>

      <Button
        variant="default"
        size="sm"
        className={styles.products_openOrder}
        onClick={() => onOpenOrder(orderId)}
      >
        {t("Open order details")}
      </Button>
    </div>
  );
};

export default ReferralOrderProducts;
